import { useMemo } from 'react';
import { Box, Card, CardContent, CardHeader, Skeleton, Typography } from '@mui/material';
import { Layers as LayersIcon } from '@mui/icons-material';
import { useDeviceSoftwareVersions } from '@/hooks/use-devices';
import { packageDisplayLabel, packageKeyOf } from '@/components/software-update/package-helpers';
import type { PackageCategory } from '@/types';

const ICON_SX = { color: '#666666', fontSize: 20 };

// Per-category presentation: label, group order, and which of the two layout
// columns the group renders in. 'other' collects unknown backend categories.
type CategoryKey = PackageCategory | 'other';

const categoryConfig: Record<CategoryKey, { label: string; order: number; column: 0 | 1 }> = {
  platform: { label: 'Platform', order: 1, column: 0 },
  system_app: { label: 'System Apps', order: 2, column: 0 },
  streaming_app: { label: 'Streaming Apps', order: 3, column: 1 },
  other: { label: 'Other', order: 4, column: 1 },
};

const categoryKeyOf = (category: string): CategoryKey =>
  category in categoryConfig ? (category as CategoryKey) : 'other';

interface SoftwareRow {
  key: string;
  displayName: string;
  category: CategoryKey;
  version: string | null;
}

interface SoftwareGroup {
  category: CategoryKey;
  label: string;
  rows: SoftwareRow[];
}

// Unified software inventory (MDM Agent + app/os/fw rows) for Device Overview.
// Backend snapshot only — no refresh/polling here; the backend refreshes the
// list after install-complete / reboot install-success events.
//
// The response is the complete inventory in backend-defined order (MDM Agent
// and every queryable package are always present, version null when not
// reported), so rows render as-is grouped by the backend-assigned category
// (Platform / System Apps / Streaming Apps).
export function DeviceSoftwareVersions({ deviceId }: { deviceId: string }) {
  const { data, isLoading, error } = useDeviceSoftwareVersions(deviceId);

  const groups = useMemo<SoftwareGroup[]>(() => {
    const byCategory = new Map<CategoryKey, SoftwareRow[]>();
    for (const v of data?.versions ?? []) {
      const row: SoftwareRow = {
        key: packageKeyOf(v),
        displayName: packageDisplayLabel(v),
        category: categoryKeyOf(v.category),
        version: v.version,
      };
      const list = byCategory.get(row.category);
      if (list) {
        list.push(row);
      } else {
        byCategory.set(row.category, [row]);
      }
    }

    return [...byCategory.entries()]
      .sort(([a], [b]) => categoryConfig[a].order - categoryConfig[b].order)
      .map(([category, categoryRows]) => ({
        category,
        label: categoryConfig[category].label,
        rows: categoryRows,
      }));
  }, [data]);

  return (
    <Card sx={{ height: '100%' }}>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LayersIcon sx={ICON_SX} />
            <Typography variant="subtitle1" fontWeight={600}>
              Software Versions
            </Typography>
          </Box>
        }
        sx={{ pb: 0 }}
      />
      <CardContent sx={{ pt: 1 }}>
        {error ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="error">
              Failed to load software versions.
            </Typography>
          </Box>
        ) : isLoading ? (
          <Box sx={{ pt: 0.5 }}>
            {Array.from({ length: 8 }, (_, i) => (
              <Skeleton key={i} height={24} sx={{ my: 0.5 }} />
            ))}
          </Box>
        ) : groups.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No software version data has been reported yet.
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              columnGap: 3.5,
              alignItems: 'start',
            }}
          >
            {[0, 1].map((col) => (
              <Box key={col}>
                {groups
                  .filter((g) => categoryConfig[g.category].column === col)
                  .map((group, i) => (
                    <Box key={group.category}>
                      <Typography
                        variant="body2"
                        component="div"
                        sx={{
                          pt: i === 0 ? 0.75 : 2,
                          pb: 0.6,
                          fontWeight: 600,
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        {group.label}
                      </Typography>
                      {group.rows.map((row, j) => (
                        <Box
                          key={row.key}
                          sx={{
                            display: 'flex',
                            alignItems: 'baseline',
                            justifyContent: 'space-between',
                            gap: 2,
                            py: 0.8,
                            borderBottom: j === group.rows.length - 1 ? 'none' : '1px solid',
                            borderColor: 'divider',
                          }}
                        >
                          <Typography variant="body2">{row.displayName}</Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              fontFamily: 'monospace',
                              color: row.version ? 'text.secondary' : 'text.disabled',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {row.version || '—'}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  ))}
              </Box>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
